import { useDispatch } from 'react-redux'
import * as R from 'ramda'
import { useDebouncedCallback } from 'use-debounce'

import type {
  ModifiedChargeSheetLineItemInput,
  RetailOrderLineItem,
  UpdateRetailOrderLineItemInput,
} from '~/api/graphql/generated/types'
import { MIN_QUANTITY } from '~/components/dashboard/soapV2/charges/constants'
import type { OptimisticOverride } from '~/components/dashboard/soapV2/charges/table/hooks/useOptimisticItemUpdates'
import { editRetailOrderLineItem } from '~/store/actions/finance'
import { editChargeSheetItem } from '~/store/duck/clientFinanceData'
import type { InvoiceLineItem } from '~/types'
import {
  calculatePrepaidColumns,
  getIsRetailOrderLineItem,
} from '~/utils/invoiceUtils'

// Quantity field supports decimal values. Ops suggested they need enough time for error correction on decimal values, hence increasing debounce time by 500ms (from default 1000ms to 1500ms).
const QUANTITY_FIELD_DEBOUNCE_MS = 1500

/**
 * Hook for handling quantity changes on charge sheet items
 * Supports both InvoiceLineItem and RetailOrderLineItem with debouncing
 * Handles prepaid items with quantity adjustment logic
 * Includes validation to prevent invalid API calls
 *
 * @param item - The line item to edit
 * @param applyOptimistic - Optional callback to apply optimistic updates before API call
 * @returns Object with onChange handler and cancel function
 */
export const useChargeItemQuantityEdit = (
  item: InvoiceLineItem | RetailOrderLineItem,
  applyOptimistic?: (itemId: string, changes: OptimisticOverride) => void,
) => {
  const dispatch = useDispatch()
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  // Debounced handler for invoice line items
  const handleEditInvoiceLineItem = useDebouncedCallback(
    (payload: { quantity: number }) => {
      const data: ModifiedChargeSheetLineItemInput = {
        ...payload,
        prepaid: (item as InvoiceLineItem).prepaid,
        id: item.id,
        expectedModification: item.modificationDate,
      }
      dispatch(editChargeSheetItem({ updateItemInput: [data] }))
    },
    QUANTITY_FIELD_DEBOUNCE_MS,
  )

  // Debounced handler for retail order line items
  const handleEditRetailOrderLineItem = useDebouncedCallback(
    (payload: { quantity: number }) => {
      const retailItem = item as RetailOrderLineItem
      const data: UpdateRetailOrderLineItemInput = {
        ...payload,
        id: retailItem.id,
        quantity: payload.quantity,
        declined: retailItem.declined,
        partNumber: retailItem.partNumber,
        producerId: retailItem.producerId,
        expectedModificationDate: retailItem.modificationDate,
        logExpectedModificationDate: retailItem.prescription?.updatedAt,
        autoshipFrequency: retailItem.autoshipFrequency,
        autoshipUnitId: retailItem.autoshipUnit?.id,
      }
      dispatch(editRetailOrderLineItem(data))
    },
    QUANTITY_FIELD_DEBOUNCE_MS,
  )

  const onChange = (value: string) => {
    // Handle empty/nil values
    if (R.isNil(value) || R.isEmpty(value)) {
      // Cancel any pending debounced calls
      handleEditInvoiceLineItem.cancel()
      handleEditRetailOrderLineItem.cancel()
      return
    }

    const numericValue = parseFloat(value)

    // Validate quantity
    if (Number.isNaN(numericValue) || numericValue < MIN_QUANTITY) {
      // Apply optimistic update for invalid value (for immediate UI feedback)
      applyOptimistic?.(item.id, { quantity: numericValue })
      // Cancel any pending debounced calls
      handleEditInvoiceLineItem.cancel()
      handleEditRetailOrderLineItem.cancel()
      return
    }

    let finalQuantity = numericValue

    // For prepaid items, calculatePrepaidColumns may adjust quantity
    // when prepaidRemaining would go negative
    if (!isRetailOrderLineItem && (item as InvoiceLineItem).prepaid) {
      const newItem = { ...item, quantity: numericValue } as InvoiceLineItem
      calculatePrepaidColumns(item as InvoiceLineItem, newItem, false)
      finalQuantity = newItem.quantity
    }

    // Apply optimistic update immediately if callback provided
    if (applyOptimistic) {
      applyOptimistic(item.id, { quantity: finalQuantity })
    }

    // Dispatch debounced API call
    if (isRetailOrderLineItem) {
      handleEditRetailOrderLineItem({ quantity: finalQuantity })
    } else {
      handleEditInvoiceLineItem({ quantity: finalQuantity })
    }
  }

  const cancel = () => {
    handleEditInvoiceLineItem.cancel()
    handleEditRetailOrderLineItem.cancel()
  }

  return { onChange, cancel }
}
